import type { TransactionErrorKey } from '@api/hooks/useBatch.ts';
import { useBatchTransactions } from '@api/hooks/useBatch.ts';
import {
	type ColumnProps,
	DEFAULT_PAGE_SIZE,
	DropdownPositions,
	Pagination,
	Table,
} from '@gurupay/gp-ui-library';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BatchStatus } from 'types/operations.ts';

import { ErrorFilter } from './ErrorFilter.tsx';
import { errorRenderers } from './errorRenderers.tsx';

interface ErrorTableProps {
	batchId: string;
	batchStatus: BatchStatus;
	isDeleteDisabled: boolean;
}

export const ErrorTable = ({ batchId }: ErrorTableProps) => {
	const { t } = useTranslation('', { keyPrefix: 'payments.batch.table' });
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [selectedErrorKeys, setSelectedErrorKeys] = useState<TransactionErrorKey[]>([]);

	const { data, isLoading } = useBatchTransactions({
		batchId,
		errorLimit: pageSize,
		errorCurrentPage: currentPage,
		filters: {
			errorKeys: selectedErrorKeys,
		},
	});

	const shouldGoToPreviousPage =
		!isLoading &&
		data &&
		currentPage > 1 &&
		data.failedToReadEntries.items.length === 0 &&
		data.failedToReadEntries.pagination.count > 0;

	const availableKeys = useMemo(
		() => [...new Set(data?.errorMessages?.map((m) => m.error) ?? [])],

		[data?.errorMessages],
	);

	useEffect(() => {
		if (shouldGoToPreviousPage) {
			setCurrentPage((prev) => prev - 1);
		}
	}, [shouldGoToPreviousPage]);

	const handlePageSizeChange = (newPageSize: number) => {
		setPageSize(newPageSize);
		setCurrentPage(1);
	};

	const PAGE_SIZE_OPTIONS = useMemo(
		() => [
			{ label: t('options.show25'), value: 25 },
			{ label: t('options.show50'), value: 50 },
			{ label: t('options.show75'), value: 75 },
		],
		[t],
	);

	const columns: ColumnProps[] = [
		{ label: t('beneficiary'), key: 'beneficiary', align: 'left' },
		{ label: t('description'), key: 'description', align: 'left' },
		{ label: t('status'), key: 'status', align: 'left' },
		{ label: t('totalAmount'), key: 'amount', align: 'right' },
	];

	const isTableLoading = isLoading;
	const { items, pagination } = data?.failedToReadEntries ?? {};

	return (
		<>
			<ErrorFilter
				availableKeys={availableKeys}
				onChange={(keys) => {
					setSelectedErrorKeys(keys);
					setCurrentPage(1);
				}}
				selectedKeys={selectedErrorKeys}
			/>
			<Table
				className="border-t border-t-neutral-100 mt-space-8"
				columns={columns}
				emptyTablePlaceholder={t('empty')}
				isLoading={isTableLoading}
				renders={errorRenderers(t)}
				rows={items ?? []}
			/>

			{pagination && pagination.count > DEFAULT_PAGE_SIZE && (
				<div className="flex justify-center mt-space-24">
					<Pagination
						currentPage={currentPage}
						dropdownOpenPosition={DropdownPositions.TOP}
						onPageChange={setCurrentPage}
						onPageSizeChange={handlePageSizeChange}
						pageOptions={PAGE_SIZE_OPTIONS}
						pageSize={pageSize}
						pageText={t('pageText')}
						totalCount={pagination.count}
						totalCountText={t('totalResults')}
					/>
				</div>
			)}
		</>
	);
};
